import {
  ContentTypeIdentifier,
  ProviderIdentifier,
  type Buttons,
  type CastMember,
  type ContentInfo,
  type EpisodeFilter,
  type Genre,
  type KtList,
  type LastWatched,
  type NpvrInfo,
  type ProgressBar,
  type Version,
  type VersionProvider
} from '@norlys-a-s/norlys-play-kmm';
import { ContentListProviders, ContentType, GetContentInfoParams } from '@smart-tv/typings/content';

import { kmm } from './kmm';

export interface NpvrInfoData {
  notSeen: boolean;
  expireAt: number;
  recordedAt: number;
  npvrTrickmodes: number;
  customerRecording: boolean;
  isSeriesRecording: boolean;
  recordingStatus: string;
  enoughSpaceForRecording: boolean;
  isNpvrExtendAllowed: boolean;
  extendNpvrEventWithInSec: number;
  npvrMediaId: number;
  expireSoon: boolean;
}

export interface ContentInfoRaw {
  contentId: number;
  title: string;
  subtitle: string;
  cover?: string;
  portraitCover: string;
  description: string;
  isCustomerFavorite: boolean;
  buttons?: {
    primary: {
      action: string;
      title: string;
    };
    secondary: {
      action: string;
      title: string;
    }[];
  };
  lastWatched?: {
    persistantTimeIndex: number;
    highestTimeIndex: number;
    lastUpdated: number;
    secondsWatched: number;
    programLength: number;
    progressBar: string;
    mediaId: number;
    eventId: number;
    sid: number;
  };
  progressBar?: {
    start: number;
    end: number;
    duration?: number;
    progress: number;
    percentage: number;
    label: string;
  };
  providers: {
    providerType: string;
    providerName: string;
    length: number;
    covers: ImageUrls;
    hasTrailer: boolean;
    rentalPeriod: number;
    unPublishDate: number;
    access: boolean;
    previewDuration: number;
    trailerMediaId: number;
    lastWatched?: {
      persistantTimeIndex: number;
      highestTimeIndex: number;
      lastUpdated: number;
      secondsWatched: number;
      programLength: number;
      progressBar?: string;
      mediaId: number;
      eventId: number;
      sid: number;
    };
    secondsWatched: number;
    missingSubscriptionText: string;
    latestWatched: boolean;
    providerKey: string;
    providerId: number;
    versions: {
      resolution: string;
      eventId: number;
      sid: number;
      price: number;
      isRented: boolean;
      language: string;
      subtitles: string;
      secondsWatched: number;
      unPublishDate: number;
      airTimeStart: number;
      npvrInfo?: NpvrInfoData;
      rentalExpire: number;
      latestWatched: boolean;
      shouldShowContinuePrompt: boolean;
      mediaId: number;
    }[];
  }[];
  descriptionHeadline: string;
  npvrInfo?: NpvrInfoData;
  contentType: string;
  programInfo: {
    startTime: number;
    endTime: number;
    catchupExpireTime: number;
    channelName: string;
    year: number;
  };
  cast: {
    personId: number;
    firstName: string;
    lastName: string;
    roleName: string;
    job: string;
  }[];
  warningText: string;
  episodeNumber: number;
  seasonNumber: number;
  episodeTitle: string;
  episodeId: number;
  episodeFilters: {
    seasonIds: number[];
    filterTitle: string;
  }[];
  trackingCategory: string;
  lessMetadata: boolean;
  totalNumberOfSeasons: number;
  totalNumberOfEpisodes: number;
  year: number;
  readableAirTimeOrRecordingTime?: string;
  readableExpireTime?: string;
  infoBlock: {
    multipleVersion: boolean;
    duration: number;
    textSeparator: string;
    year: number;
    providerName: string;
    startTime: number;
    expireTime: number;
  };
  contentMode: string;
  genre?: {
    id: number;
    genreName: string;
    subGenres: {
      id: number;
      genreName: string;
    }[];
  };
  providerAccessNeededText?: string;
}

interface ImageUrls {
  '200x180'?: string;
  '200x80'?: string;
  '233x147'?: string;
  '25x25'?: string;
  '30x30'?: string;
  '60x32'?: string;
  '60x38'?: string;
  '60x60'?: string;
  current?: string;
}

const toArray = <T>(list?: KtList<T> | null): T[] =>
  list ? Array.from(list.asJsReadonlyArrayView()) : [];

const mapContentType = (contentType?: ContentType): ContentTypeIdentifier => {
  switch (contentType) {
    case ContentType.Movies:
      return ContentTypeIdentifier.MOVIES;
    case ContentType.Series:
      return ContentTypeIdentifier.SERIES;
    case ContentType.Program:
    default:
      return ContentTypeIdentifier.PROGRAM;
  }
};

const mapProviders = (providers?: ContentListProviders): ProviderIdentifier | undefined => {
  switch (providers) {
    case ContentListProviders.All:
      return ProviderIdentifier.ALL;
    case ContentListProviders.Subscribed:
      return ProviderIdentifier.SUBSCRIBED;
    default:
      return undefined;
  }
};

export const mapNpvrInfo = (npvrInfo?: NpvrInfo | null): NpvrInfoData | undefined => {
  if (!npvrInfo) return undefined;

  return {
    notSeen: Boolean(npvrInfo.isNotSeen),
    expireAt: npvrInfo.expireTimeEpochSeconds ?? 0,
    recordedAt: npvrInfo.recordedTimeEpochSeconds ?? 0,
    npvrTrickmodes: npvrInfo.trickModes?.bitmask ?? 0,
    customerRecording: Boolean(npvrInfo.isCustomerRecording),
    isSeriesRecording: Boolean(npvrInfo.isSeriesRecording),
    recordingStatus: npvrInfo.recordingStatus ?? '',
    enoughSpaceForRecording: Boolean(npvrInfo.isEnoughSpaceForRecording),
    isNpvrExtendAllowed: Boolean(npvrInfo.isNpvrExtendAllowed),
    extendNpvrEventWithInSec: npvrInfo.extendNpvrEventWithinSeconds ?? 0,
    npvrMediaId: npvrInfo.mediaId ?? 0,
    expireSoon: Boolean(npvrInfo.expiresSoon)
  };
};

const mapLastWatched = (lastWatched?: LastWatched | null) => {
  if (!lastWatched) return undefined;

  return {
    persistantTimeIndex: lastWatched.persistentTimeIndex,
    highestTimeIndex: lastWatched.highestTimeIndex,
    lastUpdated: lastWatched.lastUpdatedEpochSeconds,
    secondsWatched: lastWatched.secondsWatched,
    programLength: lastWatched.programLength,
    progressBar: lastWatched.progressBar?.name,
    mediaId: lastWatched.mediaId ?? 0,
    eventId: lastWatched.eventId ?? 0,
    sid: lastWatched.sid ?? 0
  };
};

const mapProgressBar = (progressBar?: ProgressBar | null) => {
  if (!progressBar) return undefined;

  const start = progressBar.startEpochSeconds;
  const end = progressBar.endEpochSeconds;

  return {
    start,
    end,
    duration: end - start,
    progress: progressBar.progress,
    percentage: progressBar.percentage,
    label: progressBar.label ?? ''
  };
};

const mapVersion = (version: Version) => ({
  resolution: version.resolution,
  eventId: version.eventId ?? 0,
  sid: version.sid ?? 0,
  price: version.price,
  isRented: version.isRented,
  language: version.language,
  subtitles: version.subtitles,
  secondsWatched: version.secondsWatched,
  unPublishDate: version.unPublishDateEpochSeconds ?? 0,
  airTimeStart: version.airTimeStartEpochSeconds ?? 0,
  npvrInfo: mapNpvrInfo(version.npvrInfo),
  rentalExpire: version.rentalExpireEpochSeconds ?? 0,
  latestWatched: version.latestWatched,
  shouldShowContinuePrompt: version.shouldShowContinuePrompt,
  mediaId: version.mediaId ?? 0
});

const mapProvider = (provider: VersionProvider) => ({
  providerType: provider.providerType,
  providerName: provider.providerName,
  length: provider.length,
  covers: {
    current: provider.covers?.current ?? undefined
  },
  hasTrailer: provider.hasTrailer,
  rentalPeriod: provider.rentalPeriod,
  unPublishDate: provider.unPublishDateEpochSeconds,
  access: provider.access,
  previewDuration: provider.previewDuration ?? 0,
  trailerMediaId: provider.trailerMediaId ?? 0,
  lastWatched: mapLastWatched(provider.lastWatched),
  secondsWatched: provider.secondsWatched,
  missingSubscriptionText: provider.missingSubscriptionText ?? '',
  latestWatched: provider.latestWatched,
  providerKey: provider.providerKey,
  providerId: provider.providerId,
  versions: toArray(provider.versions).map(mapVersion)
});

const mapButtons = (buttons?: Buttons | null) => {
  if (!buttons) return undefined;

  return {
    primary: buttons.primary
      ? {
          action: buttons.primary.action.name,
          title: buttons.primary.title
        }
      : undefined,
    secondary: toArray(buttons.secondary).map((button) => ({
      action: button.action.name,
      title: button.title
    }))
  };
};

const mapCast = (castMembers: CastMember[]) =>
  castMembers.map((member) => ({
    personId: member.id,
    firstName: member.firstName ?? '',
    lastName: member.lastName ?? '',
    roleName: member.roleName ?? '',
    job: member.job ?? ''
  }));

const mapEpisodeFilters = (episodeFilters: EpisodeFilter[]) =>
  episodeFilters.map((filter) => ({
    seasonIds: toArray(filter.seasonIds),
    filterTitle: filter.title
  }));

const mapGenre = (genre?: Genre | null) => {
  if (!genre) return undefined;

  return {
    id: genre.id,
    genreName: genre.name,
    subGenres: toArray(genre.subGenres).map((subGenre) => ({
      id: subGenre.id,
      genreName: subGenre.name
    }))
  };
};

const mapContentInfo = (contentInfo: ContentInfo): ContentInfoRaw => {
  const providers = toArray(contentInfo.providers).map(mapProvider);
  const firstProvider = providers[0];
  const providerAccessNeededText = contentInfo.providerAccessNeededText;

  return {
    contentId: contentInfo.contentId,
    title: contentInfo.title,
    subtitle: contentInfo.subtitle,
    cover: contentInfo.cover ?? undefined,
    portraitCover: contentInfo.portraitCover ?? '',
    description: contentInfo.description,
    isCustomerFavorite: contentInfo.isCustomerFavorite,
    buttons: mapButtons(contentInfo.buttons) as ContentInfoRaw['buttons'],
    lastWatched: mapLastWatched(contentInfo.lastWatched) as ContentInfoRaw['lastWatched'],
    progressBar: mapProgressBar(contentInfo.progressBar),
    providers,
    descriptionHeadline: contentInfo.descriptionHeadline ?? '',
    npvrInfo: mapNpvrInfo(contentInfo.npvrInfo),
    contentType: contentInfo.contentType?.name ?? '',
    programInfo: {
      startTime: contentInfo.infoBlock?.startTimeEpochSeconds ?? 0,
      endTime: contentInfo.infoBlock?.expireTimeEpochSeconds ?? 0,
      catchupExpireTime: contentInfo.infoBlock?.expireTimeEpochSeconds ?? 0,
      channelName: firstProvider?.providerName ?? '',
      year: contentInfo.infoBlock?.year ?? 0
    },
    cast: mapCast(toArray(contentInfo.cast?.castMembers)),
    warningText: contentInfo.warningText ?? '',
    episodeNumber: contentInfo.episodeNumber ?? 0,
    seasonNumber: contentInfo.seasonNumber ?? 0,
    episodeTitle: contentInfo.episodeTitle ?? '',
    episodeId: contentInfo.episodeId ?? 0,
    episodeFilters: mapEpisodeFilters(toArray(contentInfo.episodeFilters)),
    trackingCategory: contentInfo.trackingCategory ?? '',
    lessMetadata: false,
    providerAccessNeededText: providerAccessNeededText ?? undefined,
    totalNumberOfSeasons: contentInfo.totalNumberOfSeasons,
    totalNumberOfEpisodes: contentInfo.totalNumberOfEpisodes,
    year: contentInfo.infoBlock?.year ?? 0,
    readableAirTimeOrRecordingTime: contentInfo.readableAirTimeOrRecordingTime ?? undefined,
    readableExpireTime: contentInfo.readableExpireTime ?? undefined,
    infoBlock: {
      multipleVersion: Boolean(contentInfo.infoBlock?.hasMultipleVersions),
      duration: contentInfo.infoBlock?.durationSeconds ?? 0,
      textSeparator: contentInfo.infoBlock?.textSeparator ?? '',
      year: contentInfo.infoBlock?.year ?? 0,
      providerName: contentInfo.infoBlock?.providerName ?? '',
      startTime: contentInfo.infoBlock?.startTimeEpochSeconds ?? 0,
      expireTime: contentInfo.infoBlock?.expireTimeEpochSeconds ?? 0
    },
    contentMode: contentInfo.contentInfoMode?.name ?? '',
    genre: mapGenre(contentInfo.genre) as ContentInfoRaw['genre']
  };
};

const getContentInfo = async (
  payload: GetContentInfoParams,
  handleErrorGenerally = true
): Promise<ContentInfoRaw> => {
  const providers = mapProviders(payload.providers);
  const contentInfo = await kmm(
    (repo) =>
      repo.getContentInfo(
        mapContentType(payload.contentType),
        payload.contentId,
        payload.sid,
        payload.eventId,
        payload.universeId,
        providers,
        payload.subProviderId,
        payload.providerCollectionId
      ),
    handleErrorGenerally
  );

  return mapContentInfo(contentInfo);
};

export default getContentInfo;
